import axios from 'axios'
import { Message } from '@/utils/Message'
import { Notification, MessageBox } from 'element-ui'
import store from '@/store'
import router from '@/router'
import { getToken, removeToken } from '@/utils/auth'
import { resetRouter } from '@/router'
import { cacheUrls } from '@/api/urlTranslator4cache'
import { syncSpecificBasicData } from '@/utils/basicDataCache'
import i18n from '@/lang'
import * as CommonUtil from '@/utils/commonUtil'
import { appendProblemId, getProblemId, getTraceId } from '@/utils/observability'

/**
 * 将对象中的所有null值替换为空字符串
 */
function replaceNullWithEmptyString(obj) {
  if (typeof obj !== 'object') {
    return obj
  }
  if (obj === null) {
    return ''
  }
  const visited = new WeakSet() // 用于检测循环引用
  const stack = [obj]
  try {
    while (stack.length) {
      const current = stack.pop()
      // 检测是否已经访问过该对象
      if (visited.has(current)) {
        continue // 如果已经访问过，则跳过以避免循环引用
      }
      visited.add(current)
      processValues(current, stack) // 处理当前对象或数组的值
    }
    return obj
  } catch (error) {
    return obj // 发生错误时返回原值
  }
}

/**
* 处理对象或数组中的值。
* @param {Object|Array} current - 当前对象或数组。
* @param {Array} stack - 待处理的对象栈。
*/
function processValues(current, stack) {
  if (Array.isArray(current)) {
    for (let i = 0; i < current.length; i++) {
      handleValue(current[i], i, current, stack)
    }
  } else {
    for (const key in current) {
      if (current.hasOwnProperty(key)) { // 确保只处理自身属性
        handleValue(current[key], key, current, stack)
      }
    }
  }
}

/**
* 处理单个值。
* @param {*} value - 当前值。
* @param {string|number} key - 属性名或索引。
* @param {Object|Array} parent - 父对象或数组。
* @param {Array} stack - 待处理的对象栈。
*/
function handleValue(value, key, parent, stack) {
  if (value === null) {
    parent[key] = '' // 将 null 替换为空字符串
  } else if (typeof value === 'object' && value !== null) {
    stack.push(value) // 如果是对象或数组，继续遍历
  }
}

function buildErrorMessages(errors) {
  if (!Array.isArray(errors)) {
    return ''
  }
  return [...new Set(errors.map(error => error && error.message).filter(Boolean))].join(',')
}

export function handleSessionExpired() {
  removeToken()
  resetRouter()
  Message({
    showClose: true,
    message: i18n.t('Message.SessionPleaseLogInAgain'),
    type: 'error',
    duration: 5 * 1000
  })
  setTimeout(() => {
    router.push('/login')
  }, 2000)
}

// create an axios instance
const service = axios.create({
  baseURL: '/', // process.env.VUE_APP_BASE_API, // api 的 base_url
  withCredentials: true, // 跨域请求时发送 cookies
  timeout: 7 * 60 * 1000 // request timeout
})

// request interceptor
service.interceptors.request.use(
  config => {
    // console.log('process.env.VUE_APP_BASE_API', process.env.VUE_APP_BASE_API)
    // console.log('process.env.NODE_ENV', process.env.NODE_ENV)
    // Do something before request is sent
    if (store.getters.token) {
      // 让每个请求携带token-- ['X-Token']为自定义key 请根据实际情况自行修改
      // config.headers['Authorization'] = getToken()
      config.headers['sessionId'] = getToken()
    }
    if (store.getters.language === 'zh') {
      config.headers['language'] = 'zh_CN'
    } else if (store.getters.language === 'en') {
      config.headers['language'] = 'en_US'
    } else if (store.getters.language === 'ja') {
      config.headers['language'] = 'ja_JP'
    } else if (store.getters.language === 'ru') {
      config.headers['language'] = 'ru_RU'
    } else if (store.getters.language === 'th') {
      config.headers['language'] = 'th_TH'
    } else if (store.getters.language === 'vi') {
      config.headers['language'] = 'vi_VN'
    }
    // 获取时区
    config.headers['zone'] = CommonUtil.requestTimeZone()
    return config
  },
  error => {
    // Do something with request error
    console.log('request===', error) // for debug
  }
)

// response interceptor
service.interceptors.response.use(
  response => {
    // console.log('response', response)
    const requestUrl = response.config.url
    const requestMethod = response.config.method
    const traceId = getTraceId(response)
    if (response.config.passthroughBlob) {
      return response
    }
    // 替换一些本该是[]或者{}的空值为字符串会导致序列化格式错误
    const replaceNull = response.config.replaceNull !== undefined ? response.config.replaceNull : true
    const res = replaceNull ? replaceNullWithEmptyString(response.data) : response.data
    // console.log('response===', response)
    if (res.ok === undefined && res.type) {
      // edi下载的响应方式是blog，不走正常响应流程
      if (res.type === 'application/octet-stream') {
        return response
      } else {
        // 下载失败
        return Promise.reject(i18n.t('Message.EDITemplateExceptionDownloadFailed'))
      }
    } else {
      if (!res.ok) {
        if (res.code === '0001') {
          handleSessionExpired()
          return Promise.reject(res.data)
        }
        // 因返回的错误信息需要区别对待
        let errorMsg = ''
        if (['3', '4'].includes(res.code)) {
          if (res.errors.length === 1) {
            if (res.errors[0].code === '5604101' ||
              res.errors[0].code === '5604102' ||
              res.errors[0].code === '5604104' ||
              res.errors[0].code === '5604103' ||
              res.errors[0].code === '5604105') {
              MessageBox.confirm(`${res.errors[0].message}`, i18n.t('FileHistory.Tips'), {
                confirmButtonText: i18n.t('Invoice.invoice_confirm'),
                cancelButtonText: i18n.t('Message.Cancel'),
                type: 'warning',
                dangerouslyUseHTMLString: true
              }).catch(() => {})
              return Promise.reject()
            } else if (res.errors[0].code === '0013') {
              errorMsg = i18n.t('CargoMsg.Cargo_DeleteData')
            } else if (res.errors[0].code === '110000') {
              return Promise.reject(i18n.t('SeaExport.NoImportEDI'))
            } else if (res.errors[0].code === '110001' ||
              res.errors[0].code === '110008' ||
              res.errors[0].code === '110009') {
              return Promise.reject(res.errors[0].message)
            } else {
              errorMsg = res.errors[0].message
            }
          } else if (res.errors.length > 1) {
            // 取errors信息
            errorMsg = buildErrorMessages(res.errors)
          }
          if (res.errors && Array.isArray(res.errors) && res.errors.find(err => err.code === '0001')) {
            handleSessionExpired()
            return Promise.reject(res.data)
          }
        } else if (res.code === '7') {
          return Promise.reject(res.data)
        } else if (res.code === '0004' && requestUrl && (requestUrl.indexOf('/ad/carousel/list') > 0 ||
            requestUrl.indexOf('/unread-ad-count') > 0 || requestUrl.indexOf('/unreadNum') > 0)) {
          return res
        } else {
          errorMsg = res.message
        }
        store.dispatch('log/addLog', {
          type: 'error',
          error: errorMsg,
          traceId,
          url: window.location.href,
          date: new Date().getTime(),
          from: 'request'
        })
        if (!response.config.forbidAlert) {
          Message({
            showClose: true,
            message: errorMsg,
            dangerouslyUseHTMLString: errorMsg && errorMsg.indexOf('<br/>') > -1,
            type: 'error',
            duration: 5 * 1000
          })
        }
        if (response.config.fullError) { // 返回完整的错误信息
          return Promise.reject({ ...res, errorMsg })
        } else {
          return Promise.reject(errorMsg)
        }
      } else {
        // 响应成功后 刷新缓存
        Object.keys(cacheUrls).forEach(key => {
          const currentVal = cacheUrls[key]
          const requestUrlWithoutArgs = requestUrl.split('?')[0]
          if (requestUrlWithoutArgs.endsWith(currentVal.url) && ['post', 'put', 'delete'].includes(requestMethod)) {
            // todo 刷新缓存 可以搞成同步的
            console.log('刷新缓存', requestUrlWithoutArgs, requestMethod, currentVal.cacheKey)
            syncSpecificBasicData(currentVal.cacheKey)
          }
        })

        return res
      }
    }
  },
  error => {
    // console.log('00' + error) // for debug
    let message = ''
    const { response } = error
    const traceId = getTraceId(response)
    const problemId = getProblemId(response)
    if (response && response.data) {
      message = response.data.code === 5 ? response.data.errors : response.data.message
    }
    // 如果是请求未读消息接口报错，则无需弹出提示
    let requestUrl = (response && response.config && response.config.url) ? response.config.url : ''
    let isAlert = true
    const errorUrl = error ? (error.config ? error.config.url : '') : ''
    requestUrl = !requestUrl ? errorUrl : requestUrl
    if (requestUrl && (requestUrl.endsWith('/unreadQuantity') || requestUrl.indexOf('/unread-ad-count') > 0 || requestUrl.indexOf('/ad/carousel/list') > 0 || requestUrl.indexOf('/unreadNum') > 0 || requestUrl.endsWith('syncScheduleDataToJobBySchedule') > 0)) {
      isAlert = false
    }
    // forbidAlert：本次请求由调用方统一展示错误，公共层只记日志并 reject
    if (error.config && error.config.forbidAlert) {
      isAlert = false
    }
    if (isAlert) {
      Notification.error({
        title: 'Error!',
        message: appendProblemId(message || error.message, problemId),
        offset: 50
      })
    }
    store.dispatch('log/addLog', {
      type: 'error',
      error: response ? response.data : error,
      traceId,
      url: window.location.href,
      date: new Date().getTime(),
      from: 'request'
    })
    return Promise.reject(error)
  }
)

export default service
